// CAO Studio logo components: the director's chair (the mark), the wordmark and the two lockups.
// Geometry lives in geometry.ts; motion and theme colours live in brand.css + tokens.css.
//
//   <Logo />                                  horizontal lockup, 28px tall, colours from the theme tokens
//   <Logo layout="stacked" height={160} />
//   <Mark size={40} state="listening" level={0.6} />   the mark as the Director's status
//   <Logo theme="light" />                    fixed colours (exports, emails, places without tokens)
//
// At 32 px and below (the chair's height) the small chair is drawn instead of the full one.
// Which lockup where: docs/brand/README.md (mark alone when space is tight or the name is already on screen).
import { useId, type CSSProperties } from "react";
import {
  BACK_STOPS,
  CAO,
  CHAIR,
  CHAIR_SMALL,
  CHAIR_TILE,
  LOGO_COLORS,
  TILE_SCALE,
  MARK_BOX,
  STACKED_CHAIR_SHARE,
  STUDIO,
  WORDMARK,
  chairFor,
  horizontalLockup,
  stackedLockup,
  type ChairArt,
  type LogoPaint,
  type LogoTheme,
} from "./geometry";
import "./brand.css";

/** "tokens" reads --cao-mark-frame / -under / -canvas / -canvas-2 (follows the page theme). */
export type LogoColorMode = "tokens" | LogoTheme;

/**
 * The mark's live states, matching the Director:
 * still (no motion), standby (the seat back glows like a pilot light), listening (the back takes daylight and swells
 * with your voice), thinking (the chair rocks on its feet, the back dims), speaking (the back swells with the
 * Director's voice), print (the chair sets down once: approved, done).
 */
export type MarkState = "still" | "standby" | "listening" | "thinking" | "speaking" | "print";

type Paint = LogoPaint & { word: string };
function paint(theme: LogoColorMode): Paint {
  if (theme === "tokens")
    return {
      frame: "var(--cao-mark-frame, currentColor)",
      under: "var(--cao-mark-under, currentColor)",
      canvas: "var(--cao-mark-canvas, #FFB23E)",
      canvas2: "var(--cao-mark-canvas-2, #F39A1E)",
      word: "var(--cao-mark-frame, currentColor)",
    };
  return LOGO_COLORS[theme];
}

type A11y = { title?: string; decorative?: boolean };
function a11y({ title = "CAO Studio", decorative }: A11y) {
  return decorative ? ({ "aria-hidden": true, focusable: "false" } as const) : ({ role: "img", "aria-label": title } as const);
}

/** A gradient id that is safe inside url(#...) (React's useId has punctuation in it). */
function useGradId() {
  return `cao-back-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

function ChairPaths({ p, chair, gid }: { p: Paint; chair: ChairArt; gid: string }) {
  const flat = p.canvas === p.canvas2;
  return (
    <>
      {!flat && (
        <defs>
          <linearGradient id={gid} x1="0" x2="1" y1="0" y2="0">
            {BACK_STOPS.map(([o, k]) => (
              <stop key={o} offset={o} className={`cao-chair-stop-${k}`} style={{ stopColor: p[k] }} />
            ))}
          </linearGradient>
        </defs>
      )}
      <g className="cao-chair-body">
        <g fill={p.under}>
          {chair.under.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </g>
        <g fill={p.frame}>
          {chair.posts.map((d, i) => (
            <path key={i} d={d} />
          ))}
          <path d={chair.over} />
        </g>
      </g>
      <g className="cao-chair-back">
        <path fill={flat ? p.canvas : `url(#${gid})`} d={chair.back} />
      </g>
    </>
  );
}

function WordPaths({ fill }: { fill: string }) {
  return (
    <g fill={fill} className="cao-logo-word">
      <path fillRule="evenodd" d={CAO.d} />
      <path transform={WORDMARK.studio} d={STUDIO.d} />
    </g>
  );
}

const levelStyle = (style: CSSProperties | undefined, level: number | undefined) =>
  ({ ...style, ...(level !== undefined ? { "--cao-level": Math.max(0, Math.min(1, level)).toFixed(3) } : {}) }) as CSSProperties;

export interface MarkProps extends A11y {
  size?: number | string;
  theme?: LogoColorMode;
  state?: MarkState;
  /** 0..1 voice level for listening / speaking (drives the seat back). */
  level?: number;
  /** Draw the app-icon tile behind the mark. */
  tile?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Mark({ size = 32, theme = "tokens", state = "still", level, tile, className = "", style, ...rest }: MarkProps) {
  const gid = useGradId();
  // the app-icon tile is always navy with the dark-theme mark, unless a fixed theme is asked for
  const t = tile && theme === "tokens" ? "dark" : theme;
  const p = paint(t);
  const tileFill = t === "tokens" ? LOGO_COLORS.dark.tile : LOGO_COLORS[t].tile;
  // on a tile at 48 px and below the tile chair is drawn 1:1 (it lands on whole pixels); above, the full chair at
  // TILE_SCALE of the box, as on the app icon
  const smallTile = tile && (typeof size === "number" ? size <= 48 : chairFor(size) === CHAIR_SMALL);
  const chair = tile ? (smallTile ? CHAIR_TILE : CHAIR) : chairFor(size);
  const k = tile && !smallTile ? TILE_SCALE : 1;
  return (
    <svg
      viewBox={`0 0 ${MARK_BOX} ${MARK_BOX}`}
      width={size}
      height={size}
      className={`cao-mark cao-mark-${state} ${className}`}
      style={levelStyle(style, level)}
      {...a11y(rest)}
    >
      {tile && <rect width={MARK_BOX} height={MARK_BOX} rx={MARK_BOX * 0.225} fill={tileFill} />}
      {k !== 1 ? (
        <g transform={`translate(32 32) scale(${k}) translate(-32 -32)`}>
          <ChairPaths p={p} chair={chair} gid={gid} />
        </g>
      ) : (
        <ChairPaths p={p} chair={chair} gid={gid} />
      )}
    </svg>
  );
}

export interface LogoProps extends A11y {
  layout?: "horizontal" | "stacked" | "mark" | "wordmark";
  /** Rendered height in px (or any CSS length): the whole lockup's height (horizontal: the chair's). */
  height?: number | string;
  theme?: LogoColorMode;
  /** Animate the mark inside the lockup (e.g. "standby" on the splash while the app loads). */
  state?: MarkState;
  className?: string;
  style?: CSSProperties;
}

// the lockups only ever come in these shapes, so build them once
const LOCKUPS = {
  horizontal: { full: horizontalLockup(CHAIR), small: horizontalLockup(CHAIR_SMALL) },
  stacked: { full: stackedLockup(CHAIR), small: stackedLockup(CHAIR_SMALL) },
};

export function Logo({ layout = "horizontal", height = 28, theme = "tokens", state = "still", className = "", style, ...rest }: LogoProps) {
  const gid = useGradId();
  if (layout === "mark") return <Mark size={height} theme={theme} state={state} className={className} style={style} {...rest} />;
  const p = paint(theme);
  if (layout === "wordmark") {
    return (
      <svg viewBox={WORDMARK.viewBox} height={height} className={`cao-logo cao-logo-wordmark ${className}`} style={style} {...a11y(rest)}>
        <WordPaths fill={p.word} />
      </svg>
    );
  }
  // the chair's rendered height decides which chair: the whole lockup (horizontal) or its share of it (stacked)
  const chairPx = typeof height === "number" && layout === "stacked" ? height * STACKED_CHAIR_SHARE : height;
  const chair = chairFor(chairPx);
  const L = LOCKUPS[layout][chair === CHAIR_SMALL ? "small" : "full"];
  return (
    <svg viewBox={L.viewBox} height={height} className={`cao-logo cao-logo-${layout} cao-mark-${state} ${className}`} style={style} {...a11y(rest)}>
      <g transform={L.mark}>
        <ChairPaths p={p} chair={chair} gid={gid} />
      </g>
      <g transform={L.word}>
        <WordPaths fill={p.word} />
      </g>
    </svg>
  );
}
